import React from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import moment from 'moment';
import { useSelector } from 'react-redux';
import Priority from 'components/Priority';
import { Box, Typography } from '@material-ui/core';
import { CollapseCard } from 'components/UI';
import i18n from 'i18n-js';

import { useStyles, collapseCardStyles } from './item.styled';

const ItemDetails = ({
  id,
  user,
  date,
  type,
  title,
  idLabel,
  instance,
  priority,
  dateLabel,
  userLabel,
  description,
  collapsible,
  handleItemClick,
  footerDirection,
  collapsibleContent,
}) => {
  const classes = useStyles();
  const collapseCardClasses = collapseCardStyles();
  const { localeFormat } = useSelector((state) => state.organizations);

  const getItemString = () => {
    switch (instance.type) {
      case 'ReservationDriver':
        return `${i18n.t('instance.reservation')}: ${instance.id}`;
      case 'Vehicle':
        return `${i18n.t('instance.vehicle')}: ${instance.id}`;
      case 'Driver':
        return `${i18n.t('instance.driver')}: ${instance.id}`;
      case 'Invoice':
        return `${i18n.t('instance.invoice')}: ${instance.id}`;
      case 'Subscription':
        return `${i18n.t('instance.subscription')}: ${instance.id}`;
      default:
        return instance.id;
    }
  };

  const MainComponent = () => (
    <Box
      className={clsx(
        classes.container,
        classes?.[priority],
        classes?.[type],
        collapsible ? classes.collapsibleContainer : ''
      )}
      onClick={() => handleItemClick(id)}
    >
      <Box className={classes.header} width="100%">
        <Typography color="textPrimary" style={{ lineHeight: 1 }}>
          <Box fontSize={12} fontWeight="fontWeightMedium" component="span" lineHeight={1}>
            {idLabel || `Id: ${id}`}
          </Box>
        </Typography>
        {priority && (
          <Priority placeholder={''} gutterBottom={false} priority={priority} fontSize={11} />
        )}
      </Box>
      <Box className={classes.content} width="100%">
        {title && (
          <Typography className={classes.textSecondary}>
            <Box fontSize={13} fontWeight="fontWeightBold" component="span">
              {title}
            </Box>
          </Typography>
        )}
        {description && (
          <Typography className={classes.textSecondary}>
            <Box fontSize={11} fontWeight="fontWeightLight" lineHeight={1} component="span">
              {description}
            </Box>
          </Typography>
        )}
      </Box>
      <Box
        className={clsx(
          classes.footer,
          footerDirection === 'row' ? classes.footerRow : '',
          collapsible ? classes.collapsible : ''
        )}
        width="100%"
      >
        <Typography color="textSecondary">
          <Box fontSize={12} component="span">
            {`${dateLabel} ${moment(date).format(`${localeFormat} hh:mm a`)}`}
          </Box>
        </Typography>
        <Typography color="textSecondary">
          <Box fontSize={12} component="span">
            {user?.first_name &&
              user?.last_name &&
              `${userLabel} ${user?.first_name} ${user?.last_name}`}
            {instance && getItemString()}
          </Box>
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {collapsible ? (
        <CollapseCard title={<MainComponent />} customClasses={collapseCardClasses}>
          <>{collapsibleContent}</>
        </CollapseCard>
      ) : (
        <MainComponent />
      )}
    </>
  );
};

ItemDetails.propTypes = {
  collapsible: PropTypes.bool,
  collapsibleContent: PropTypes.any,
  userLabel: PropTypes.string,
  dateLabel: PropTypes.string,
  description: PropTypes.string,
  user: PropTypes.any,
  date: PropTypes.any,
  footerDirection: PropTypes.oneOf(['row', 'column']),
  instance: PropTypes.object,
  id: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  handleItemClick: PropTypes.func.isRequired,
  priority: PropTypes.oneOf(['LOW', 'HIGH', 'MEDIUM', 'URGENT']),
};

ItemDetails.defaultProps = {
  collapsible: false,
  collapsibleContent: null,
  dateLabel: 'Open since',
  userLabel: 'Assigned to',
  footerDirection: 'row',
  handleItemClick: () => {},
};

export default ItemDetails;
